import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import tw from "tailwind-styled-components";
import {
    BoxContainer,
    BoxTitle,
    BoxHead,
    Underline,
    FilterTableWrapper,
    FilterWrapperWithSearch,
} from "../../Components/Styles/PageStyles";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import axios from "axios";
import Config from "../../Config";
import Loading from "../../Components/Loading";
import NotFoundModel from "../../Components/NotFoundModel";
import SearchFilterBox from "../../Components/SearchFilterBox";
import SideModal from "../../Components/SideModal";
import Table from "../../Components/Inventory/Items/Table";
import AddItem from "../../Components/Inventory/Items/AddItem";
import { Form, Formik } from "formik";
import EditItem from "../../Components/Inventory/Items/EditItem";
import DeleteItem from "../../Components/Inventory/Items/DeleteItem";

const FoodItems = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isShowAddItem, setIsShowAddItem] = useState(false);
    const [isShowEditItem, setIsShowEditItem] = useState(false);
    const [isDeleteItem, setIsDeleteItem] = useState(false);
    const user = useSelector((state) => state.UserReducer.user);
    const [apiData, setApiData] = useState([]);
    const [itemList, setItemList] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterType, setFilterType] = useState("");
    const searchOptions = [
        { label: "Item", value: 1 },
        // { label: "Kitchen", value: 2 },
    ];
    const [selectedData, setSelectedData] = useState("");
    const panelRef = useRef(null);
    const btnRef = useRef(null);

    // Fetch items
    const fetchFunction = async () => {
        const params = new URLSearchParams();

        if (searchQuery) {
            params.append("s", searchQuery);
            if (filterType) {
                params.append("t", filterType);
            }
        }

        const response = await axios.get(
            `${Config.apiUrl}/kitchen/inventory/item/list?${params.toString()}`,
            {
                headers: {
                    authorization: `Bearer ${user.token}`,
                    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
            }
        );
        return response.data;
    };

    const getSuccess = (res) => {
        if (res.isSuccess === false) {
            toast.error(res.msg || "Failed to fetch food items");
        } else {
            const inventoryItems = res.items || [];
            setApiData(inventoryItems);
        }
    };

    const getError = () => {
        navigate("/no-access");
    };

    const {
        isLoading: isFetching,
        mutate: getListMutate,
        error,
        data,
        isRefetching,
    } = useMutation(fetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });

    useEffect(() => {
        const handler = setTimeout(() => {
            getListMutate();
        }, 200);
        return () => clearTimeout(handler);
    }, [searchQuery, filterType,]);

    // Handle search
    const handleFilterChange = (values) => {
        setSearchQuery(values.searchQuery || "");
        setFilterType(values.filterType || "");
    };

    // Handle date range submit
    const submitHandler = (values) => {
        setFilterType("");
        setSearchQuery("");
    };

    // Clear all filters
    const handleClearFilters = () => {
        setSearchQuery("");
        setFilterType("");
    };

    //------- Add Item -------
    const addItemFunction = async (values) => {
        return await axios.post(`${Config.apiUrl}/kitchen/inventory/item/add`, values, {
            headers: {
                authorization: "Bearer" + " " + user.token,
                // "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                "Content-Type": "application/json",
            },
        });
    };

    const onAddItemSuccess = (data) => {
        if (data?.data?.isSuccess === true) {
            toast.success(data?.data?.msg || "Success");
            getListMutate();
            setIsShowAddItem(false);
        } else {
            //   setIsShowAddTransfer(false);
            toast.error(data?.data?.msg || "An Error Occured");
        }
    };

    const onAddItemError = (data) => {
        toast.error(data?.response?.data?.msg || "An Error Occured");
    };

    const { isLoading: addItemLoading, mutate: addItemMutate } = useMutation(
        addItemFunction,
        {
            onSuccess: onAddItemSuccess,
            onError: onAddItemError,
        }
    );

    //------- Edit Item -------
    const editItemFunction = async (values) => {
        return await axios.post(`${Config.apiUrl}/kitchen/inventory/item/edit`, values, {
            headers: {
                authorization: "Bearer" + " " + user.token,
                // "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                "Content-Type": "application/json",
            },
        });
    };

    const onEditItemSuccess = (data) => {
        if (data?.data?.isSuccess === true) {
            toast.success(data?.data?.msg || "Success");
            getListMutate();
            setIsShowEditItem(false);
        } else {
            //   setIsShowAddTransfer(false);
            toast.error(data?.data?.msg || "An Error Occured");
        }
    };

    const onEditItemError = (data) => {
        toast.error(data?.response?.data?.msg || "An Error Occured");
    };

    const { isLoading: editItemLoading, mutate: editItemMutate } = useMutation(
        editItemFunction,
        {
            onSuccess: onEditItemSuccess,
            onError: onEditItemError,
        }
    );

    //------- Delete Item -------
    const deleteItemFunction = async (values) => {
        return await axios.post(`${Config.apiUrl}/kitchen/inventory/item/delete`, values, {
            headers: {
                authorization: "Bearer" + " " + user.token,
                "Content-Type": "application/json",
            },
        });
    };

    const onDeleteItemSuccess = (data) => {
        if (data?.data?.isSuccess === true) {
            toast.success(data?.data?.msg || "Success");
            getListMutate();
            setIsDeleteItem(false);
        } else {
            toast.error(data?.data?.msg || "An Error Occured");
        }
    };

    const onDeleteItemError = (data) => {
        toast.error(data?.response?.data?.msg || "An Error Occured");
    };

    const { isLoading: deleteItemLoading, mutate: deleteItemMutate } =
        useMutation(deleteItemFunction, {
            onSuccess: onDeleteItemSuccess,
            onError: onDeleteItemError,
        });

    return (
        <BoxContainer>
            <BoxHead>
                <BoxTitle className="!mb-0 whitespace-nowrap">
                    Items
                    <Underline />
                </BoxTitle>
                <Filters
                    setIsShowAddItem={setIsShowAddItem}
                />
            </BoxHead>


            <FilterTableWrapper>
                <FilterWrapperWithSearch>
                    <div className="flex md:space-x-4 md:flex-row flex-col md:gap-0 gap-4">
                        <div className="flex items-center">
                            <Text>
                                Total items:{" "}
                                <span className="font-semibold">
                                    {apiData?.length || 0}
                                </span>
                            </Text>
                        </div>
                    </div>
                    <div className="flex gap-2 justify-end items-center">
                        <SearchFilterBox
                            options={searchOptions}
                            handleFilterChange={handleFilterChange}
                            handleClearFilters={handleClearFilters}
                            className="flex-1 min-w-0"
                        />
                    </div>
                </FilterWrapperWithSearch>

                <SideModal
                    isOpen={isShowAddItem}
                    onClose={() => setIsShowAddItem(false)}
                >
                    <AddItem
                        handleClose={() => setIsShowAddItem(false)}
                        addItemMutate={addItemMutate}
                        addItemLoading={addItemLoading}
                        setIsShowAddItem={setIsShowAddItem}
                    />
                </SideModal>
                <SideModal
                    isOpen={isShowEditItem}
                    onClose={() => setIsShowEditItem(false)}
                >
                    <EditItem
                        handleClose={() => setIsShowEditItem(false)}
                        editItemMutate={editItemMutate}
                        editItemLoading={editItemLoading}
                        setIsShowEditItem={setIsShowEditItem}
                        selectedData={selectedData}
                    />
                </SideModal>

                {isDeleteItem && (
                    <DeleteItem
                        selectedData={selectedData}
                        setIsDeleteItem={setIsDeleteItem}
                        deleteItemMutate={deleteItemMutate}
                        deleteItemLoading={deleteItemLoading}
                    />
                )}

                {!error && !isFetching && data && (
                    <TableWrapper>
                        <Table
                            apiData={error ? [] : apiData}
                            selectedData={selectedData}
                            setSelectedData={setSelectedData}
                            setIsShowEditItem={setIsShowEditItem}
                            setIsDeleteItem={setIsDeleteItem}
                        />
                        {(error || apiData.length === 0) && !isFetching && (
                            <NotFoundModel msg={"No items found."} />
                        )}
                    </TableWrapper>
                )}
            </FilterTableWrapper>

            {(isFetching || isRefetching) && <Loading />}
        </BoxContainer>
    );
};
const Filters = ({
    setIsShowAddItem,
}) => {
    return (
        <Formik >
            <Form>
                <FilterContainer>
                    <AddBtn
                        type="button"
                        onClick={() => {
                            setIsShowAddItem(true);
                        }}
                    >
                        {"Add Item"}
                    </AddBtn>
                </FilterContainer>
            </Form>
        </Formik>
    );
};
const TableWrapper = tw.h1`mt-4 bg-white rounded-md`;
const FilterBtn = tw.button`flex items-center border whitespace-nowrap border-gray-300 justify-center rounded-md px-4 cursor-pointer bg-white hover:bg-gray-200 h-10 text-sm ml-auto`;
const AddBtn = tw.button`${(p) =>
    p.$active
        ? "bg-gray-100"
        : ""} border whitespace-nowrap border-gray-200 flex space-x-1.5 justify-center items-center text-white h-full rounded-md px-6  cursor-pointer bg-gradient-to-r from-primary-color to-[#00A0FF] hover:brightness-110 py-2.5 text-sm`;
const FilterContainer = tw.div`grid grid-cols-2 md:grid-cols-3 w-full gap-2 lg:gap-0 md:flex md:space-x-4 items-end mt-0 md:mt-0`;
const Text = tw.p`text-gray-500 text-sm font-normal`;

export default FoodItems;